// Pure, React-free logic for the app-freshness system: outdated checks,
// version.json parsing, and the bounded-retry reload state machine. Consumed by
// the AppFreshnessGuard component. The baked-release read lives in @/lib/env
// (getRelease), shared with analytics so the "unknown"-means-dev contract has one home.
import { UNKNOWN_RELEASE } from '@/lib/env'

/** Min gap between freshness checks triggered by route navigation. */
export const THROTTLE_MS = 60_000

/** Background poll interval while the tab stays open with no navigation. */
export const POLL_INTERVAL_MS = 16 * 60 * 1000

/** Reload attempts allowed for one target version before giving up. */
export const MAX_RELOAD_ATTEMPTS = 3

/** Minimum gap between consecutive reload attempts for the same target version. */
export const RELOAD_COOLDOWN_MS = 60_000

/** Baked-version value that means "unbaked local dev build" — freshness checks no-op on it. */
export const DEV_VERSION = UNKNOWN_RELEASE

/** True when the fetched manifest names a real, different release than this bundle's baked one. */
export function isOutdated(baked: string, fetched: string): boolean {
  return baked !== DEV_VERSION && fetched.length > 0 && fetched !== baked
}

/** Validates version.json's shape before trusting it; fails open (null) on anything unexpected. */
export function parseVersionManifest(json: unknown): string | null {
  if (typeof json !== 'object' || json === null) return null
  const { version } = json as { version?: unknown }
  if (typeof version !== 'string' || version.length === 0) return null
  return version
}

/** Persisted (sessionStorage) state for the bounded-retry reload guard. */
export interface ReloadAttemptRecord {
  target: string
  attempts: number
  lastAttemptAt: number
}

export interface DecideReloadArgs {
  record: ReloadAttemptRecord | null
  targetVersion: string
  now: number
}

export interface ReloadDecision {
  shouldReload: boolean
  gaveUp: boolean
  nextRecord: ReloadAttemptRecord
}

/**
 * Bounded-retry state machine for the reload-loop guard: a null/changed target
 * resets the budget; the same target waits out RELOAD_COOLDOWN_MS between
 * attempts and gives up once MAX_RELOAD_ATTEMPTS is reached.
 */
export function decideReload({ record, targetVersion, now }: DecideReloadArgs): ReloadDecision {
  if (record === null || record.target !== targetVersion) {
    return {
      shouldReload: true,
      gaveUp: false,
      nextRecord: { target: targetVersion, attempts: 1, lastAttemptAt: now },
    }
  }

  if (record.attempts >= MAX_RELOAD_ATTEMPTS) {
    return { shouldReload: false, gaveUp: true, nextRecord: record }
  }

  const msSinceLastAttempt = now - record.lastAttemptAt
  if (msSinceLastAttempt < RELOAD_COOLDOWN_MS) {
    return { shouldReload: false, gaveUp: false, nextRecord: record }
  }

  return {
    shouldReload: true,
    gaveUp: false,
    nextRecord: { target: targetVersion, attempts: record.attempts + 1, lastAttemptAt: now },
  }
}
